import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, ArrowRight, ArrowLeft, Spinner, ShieldCheck } from "@phosphor-icons/react";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [stage, setStage] = useState("email"); // email | code
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "");
  const [isNew, setIsNew] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [business, setBusiness] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e?.preventDefault();
    setError("");
    setNotice("");
    if (!email.trim()) return setError("Please enter your email.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-code", { email: email.trim() });
      setIsNew(data.is_new_user);
      setStage("code");
      setNotice(`We sent a 6-digit code to ${data.email}.`);
      setCooldown(45);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setError("");
    if (code.trim().length !== 6) return setError("Enter the 6-digit code from your email.");
    if (isNew && (!name.trim() || !business.trim()))
      return setError("Please add your name and business name.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify", {
        email: email.trim(),
        code: code.trim(),
        name: name.trim(),
        business_name: business.trim(),
      });
      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen camino-dark flex flex-col items-center justify-center px-6 py-12">
      <img src={`${API}/assets/logo-light`} alt="CaminoSign" style={{ height: 40 }} className="mb-8" />

      <div className="w-full max-w-md rounded-2xl bg-white p-8 paper-shadow" data-testid="login-card">
        {stage === "email" ? (
          <form onSubmit={requestCode} className="space-y-5">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-navy">Sign in or create an account</h1>
              <p className="mt-2 text-sm text-slate-500">
                Enter your email and we'll send you a secure login code — no password to remember.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</Label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="h-11 pl-10"
                  autoFocus
                  data-testid="login-email-input"
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="send-code-btn">
              {loading ? <><Spinner size={18} className="animate-spin" /> Sending…</> : <>Send login code <ArrowRight size={16} weight="bold" /></>}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-5">
            <button type="button" onClick={() => { setStage("email"); setCode(""); setError(""); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="back-to-email-btn">
              <ArrowLeft size={15} /> Use a different email
            </button>
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-navy">Enter your code</h1>
              {notice && <p className="mt-2 text-sm text-slate-500" data-testid="code-notice">{notice}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">6-digit code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                className="h-12 text-center text-2xl tracking-[0.5em] font-semibold"
                autoFocus
                data-testid="login-code-input"
              />
            </div>
            {isNew && (
              <div className="space-y-4 rounded-lg bg-slate-50 p-4 border border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Welcome! Let's set up your account</p>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Your name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="h-11" data-testid="login-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Business name</Label>
                  <Input value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="Acme Realty" className="h-11" data-testid="login-business-input" />
                </div>
              </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="verify-code-btn">
              {loading ? <><Spinner size={18} className="animate-spin" /> Verifying…</> : <>{isNew ? "Create account" : "Sign in"} <ArrowRight size={16} weight="bold" /></>}
            </Button>
            <button type="button" onClick={requestCode} disabled={loading || cooldown > 0} className="w-full text-center text-sm text-action hover:underline disabled:no-underline disabled:text-slate-400 disabled:cursor-not-allowed" data-testid="resend-code-btn">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
            </button>
          </form>
        )}
      </div>

      <div className="mt-6 flex items-center gap-1.5 text-xs text-white/50">
        <ShieldCheck size={14} weight="duotone" /> Secured by CaminoSign
      </div>
    </div>
  );
}
